// src/pages/tienda/BrandCustomizationPageRefactored.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSpinner, FaEnvelope, FaGlobe, FaCreditCard, FaArrowLeft } from 'react-icons/fa';
import useAuthStore from '../../store/authStore';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';

// Hooks personalizados
import {
    useEmailCustomization,
    usePublicPageCustomization,
    useTicketCustomization
} from '../../hooks/brandCustomization';

// Componentes compartidos
import { TabNavigation } from '../../components/brandCustomization/shared';

// Componentes de Email
import { EmailTemplateCard } from '../../components/brandCustomization/EmailCustomization';
import EmailTemplateSelector from '../../components/brandCustomization/EmailCustomization/EmailTemplateSelector';

// Componentes de Public Pages
import { PublicPageCard } from '../../components/brandCustomization/PublicPageCustomization';
import PublicPageSelector from '../../components/brandCustomization/PublicPageCustomization/PublicPageSelector';

// Componentes de Ticket
import { TicketCustomizationForm } from '../../components/brandCustomization/TicketCustomization';

const BrandCustomizationPage = () => {
    const navigate = useNavigate();
    const { marcaId } = useParams();
    const { user } = useAuthStore();

    const [activeTab, setActiveTab] = useState('emails');
    const [marca, setMarca] = useState(null);
    const [loadingMarca, setLoadingMarca] = useState(true);

    // Hooks personalizados para cada sección
    const emailCustomization = useEmailCustomization(marcaId);
    const publicPageCustomization = usePublicPageCustomization(marcaId);
    const ticketCustomization = useTicketCustomization(marcaId);

    // Preview states (no necesarios en versión refactorizada, pero mantenemos para evitar errores)
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewHtml, setPreviewHtml] = useState(null);
    const [previewComponent, setPreviewComponent] = useState(null);

    // Cargar datos de la marca
    useEffect(() => {
        const fetchMarca = async () => {
            if (!user || !marcaId) return;

            setLoadingMarca(true);
            try {
                const marcaData = await get(`marcas/${marcaId}`);
                setMarca(marcaData);
            } catch (error) {
                console.error('Error cargando marca:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo cargar la información de la marca',
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            } finally {
                setLoadingMarca(false);
            }
        };

        fetchMarca();
    }, [user, marcaId]);

    // Tabs de navegación
    const tabs = [
        {
            id: 'emails',
            label: 'Emails',
            icon: FaEnvelope,
            count: 5 // ticketCreation, ticketResend, addFranchise, addWorker, winback
        },
        {
            id: 'publicPages',
            label: 'Páginas Públicas',
            icon: FaGlobe,
            count: 3 // findTicket, productos, qrRegister
        },
        {
            id: 'ticket',
            label: 'Tarjeta de Puntos',
            icon: FaCreditCard
        }
    ];

    // Loading state
    if (loadingMarca || emailCustomization.loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FaSpinner className="text-4xl text-blue-600 mb-4 mx-auto animate-spin" />
                    <p className="text-gray-600">Cargando personalizaciones...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition-colors"
                    >
                        <FaArrowLeft className="mr-2" />
                        Volver
                    </button>

                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Personalización de Marca
                            </h1>
                            <p className="text-gray-600 mt-2">
                                Personaliza los emails, páginas públicas y tarjetas de {marca?.nombre || 'tu marca'}
                            </p>
                        </div>

                        {marca?.nombre && (
                            <div className="hidden md:block px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-600 font-medium">{marca.nombre}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tab Navigation */}
                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                />

                {/* Content por Tab */}
                <div className="mt-8">
                    {/* EMAILS TAB - Refactorizado con componentes nuevos */}
                    {activeTab === 'emails' && (
                        <EmailsSection
                            emailCustomization={emailCustomization}
                            marca={marca}
                        />
                    )}

                    {/* PUBLIC PAGES TAB - Completamente refactorizado */}
                    {activeTab === 'publicPages' && (
                        <PublicPagesSection
                            publicPageCustomization={publicPageCustomization}
                            marca={marca}
                            setIsPreviewing={setIsPreviewing}
                            setPreviewComponent={setPreviewComponent}
                            setPreviewHtml={setPreviewHtml}
                        />
                    )}

                    {/* TICKET TAB - Completamente refactorizado */}
                    {activeTab === 'ticket' && (
                        <TicketSection
                            ticketCustomization={ticketCustomization}
                            marcaId={marcaId}
                            marca={marca}
                            isPreviewing={isPreviewing}
                            setIsPreviewing={setIsPreviewing}
                            setPreviewComponent={setPreviewComponent}
                            setPreviewHtml={setPreviewHtml}
                        />
                    )}
                </div>
            </div>

            {/* Preview Modal para Public Pages y Tickets (original) */}
            {isPreviewing && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div
                            className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity"
                            onClick={() => {
                                setIsPreviewing(false);
                                setPreviewComponent(null);
                                setPreviewHtml(null);
                            }}
                        />
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                                            Vista Previa
                                        </h3>
                                        <div className="mt-2">
                                            {previewComponent}
                                            {previewHtml && (
                                                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    onClick={() => {
                                        setIsPreviewing(false);
                                        setPreviewComponent(null);
                                        setPreviewHtml(null);
                                    }}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * EmailsSection - Sección de emails completamente refactorizada
 * Con selector visual para elegir qué email personalizar
 */
const EmailsSection = ({ emailCustomization, marca }) => {
    const templates = ['ticketCreation', 'ticketResend', 'addFranchise', 'addWorker', 'winback'];
    const [selectedTemplate, setSelectedTemplate] = useState(templates[0] || null);

    return (
        <div>
            {/* Selector de templates */}
            <EmailTemplateSelector
                templates={templates}
                selectedTemplate={selectedTemplate}
                onSelect={setSelectedTemplate}
                formData={emailCustomization.formData}
            />

            {/* Formulario del template seleccionado */}
            {selectedTemplate && (
                <div className="mt-6 transition-opacity duration-300">
                    <EmailTemplateCard
                        key={selectedTemplate}
                        templateName={selectedTemplate}
                        formData={emailCustomization.formData[selectedTemplate]}
                        onUpdate={emailCustomization.updateField}
                        onToggleActive={emailCustomization.toggleActive}
                        onSave={emailCustomization.save}
                        onReset={emailCustomization.reset}
                        onPreview={emailCustomization.preview}
                        isSaving={emailCustomization.isSaving}
                        marca={marca}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * PublicPagesSection - Sección de páginas públicas completamente refactorizada
 * Con selector visual para elegir qué página personalizar
 */
const PublicPagesSection = ({ publicPageCustomization, marca, setIsPreviewing, setPreviewComponent, setPreviewHtml }) => {
    const pages = ['findTicket', 'productos', 'qrRegister'];
    const [selectedPage, setSelectedPage] = useState(pages[0] || null);

    return (
        <div>
            {/* Selector de páginas */}
            <PublicPageSelector
                pages={pages}
                selectedPage={selectedPage}
                onSelect={setSelectedPage}
                formData={publicPageCustomization.formData}
            />

            {/* Formulario de la página seleccionada */}
            {selectedPage && (
                <div className="mt-6 transition-opacity duration-300">
                    <PublicPageCard
                        key={selectedPage}
                        pageName={selectedPage}
                        formData={publicPageCustomization.formData[selectedPage]}
                        onUpdate={publicPageCustomization.updateField}
                        onToggleActive={publicPageCustomization.toggleActive}
                        onSave={publicPageCustomization.save}
                        onReset={publicPageCustomization.reset}
                        onPreview={async (pageName) => {
                            try {
                                const result = await publicPageCustomization.preview(pageName, marca);
                                setIsPreviewing(true);
                                setPreviewComponent(result.component);
                                setPreviewHtml(result.html);
                            } catch (error) {
                                Swal.fire({
                                    title: 'Error',
                                    text: error.message || 'No se pudo generar la previsualización',
                                    icon: 'error',
                                    confirmButtonText: 'Aceptar'
                                });
                            }
                        }}
                        isSaving={publicPageCustomization.isSaving}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * TicketSection - Sección de tarjeta completamente refactorizada
 */
const TicketSection = ({ ticketCustomization, marcaId, marca, isPreviewing, setIsPreviewing, setPreviewComponent, setPreviewHtml }) => {
    const handlePreview = () => {
        setIsPreviewing(true);
        setPreviewHtml(null);

        try {
            const previewComponent = ticketCustomization.preview(marca);
            setPreviewComponent(previewComponent);
        } catch (error) {
            console.error('Error generando preview de ticket:', error);
            setPreviewComponent(null);
        }
    };

    // Regenerar preview automáticamente cuando cambian los datos (igual que el original)
    useEffect(() => {
        if (isPreviewing) {
            try {
                const previewComponent = ticketCustomization.preview(marca);
                setPreviewComponent(previewComponent);
            } catch (error) {
                console.error('Error regenerando preview de ticket:', error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPreviewing, ticketCustomization.formData, marca]);

    return (
        <div>
            <TicketCustomizationForm
                formData={ticketCustomization.formData}
                onUpdate={ticketCustomization.updateField}
                onToggleActive={ticketCustomization.toggleActive}
                onSave={ticketCustomization.save}
                onReset={ticketCustomization.reset}
                onPreview={handlePreview}
                isSaving={ticketCustomization.isSaving}
                marcaId={marcaId}
                onRefresh={ticketCustomization.fetchData}
                onFetchHeroImageOnly={ticketCustomization.fetchHeroImageOnly}
            />
        </div>
    );
};

export default BrandCustomizationPage;

